import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Label,
} from "@headlessui/react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n/config";
import { supportedLocaleCode } from "../../../i18n/locUtils";
import LocaleMenuEntry from "../locale-menu-entry";
import useBreakpoint from "../../utils/use-breakpoint";

const LanguageSelectionMenu = () => {
  const { t } = useTranslation();
  const breakpoint = useBreakpoint();
  const isMobile = ["xs", "sm", "md"].includes(breakpoint);
  const buttonRef = useRef<HTMLElement | null>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const [isOpen, setIsOpen] = useState(false);

  // Get native language names dynamically from translation resources
  const getNativeLanguageName = (languageCode: string) => {
    const resources = i18n.services.resourceStore.data;

    // Map the language codes to the correct locale codes and translation keys
    const languageMapping = {
      english_us: { locale: "en", key: "ui_general_language_english_us" },
      spanish: { locale: "es", key: "ui_general_language_spanish" },
      ilocano: { locale: "ilo", key: "ui_general_language_ilocano" },
      tagalog: { locale: "tl", key: "ui_general_language_tagalog" },
    };

    const mapping =
      languageMapping[languageCode as keyof typeof languageMapping];
    if (mapping) {
      const translation = resources[mapping.locale]?.translation;
      if (translation && typeof translation === "object") {
        return (translation as any)[mapping.key] || languageCode;
      }
    }
    return languageCode;
  };

  useEffect(() => {
    if (!isMobile && buttonRef.current) {
      const updatePosition = () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (rect) {
          setPanelStyle({
            top: `${rect.top}px`,
            left: `${rect.right + 4}px`,
          });
        }
      };
      updatePosition();
      window.addEventListener("scroll", updatePosition);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isMobile, isOpen]);

  // Close panel when clicking outside (for portal version)
  useEffect(() => {
    if (!isMobile && isOpen) {
      const handleClickCapture = (event: MouseEvent) => {
        const target = event.target as Node;
        const isInsidePortal = portalRef.current?.contains(target);
        
        // If click is inside portal, stop it from reaching other handlers
        if (isInsidePortal) {
          event.stopPropagation();
        }
      };
      
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        const element = event.target as Element;
        const isInsideButton = buttonRef.current?.contains(target);
        const isInsidePortal = portalRef.current?.contains(target);
        const isInsideDialog = element?.closest('[role="dialog"]');
        
        // Only close if click is outside both button and portal menu
        if (!isInsideButton && !isInsidePortal && !isInsideDialog) {
          setTimeout(() => {
            if (portalRef.current && isOpen) {
              const disclosureButton = buttonRef.current?.closest('[data-headlessui-state]')?.querySelector('button, [role="button"]');
              if (disclosureButton) {
                (disclosureButton as HTMLElement).click();
              }
            }
          }, 10);
        }
      };
      
      document.addEventListener("click", handleClickCapture, true);
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickCapture, true);
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }, [isMobile, isOpen]);

  const renderPanelContent = () => (
    <div className="px-2 py-1 space-y-1">
      <LocaleMenuEntry
        code={supportedLocaleCode.en}
        labelText={getNativeLanguageName("english_us")}
        focus={false}
        close={() => {}}
        disabled={false}
      />
      <LocaleMenuEntry
        code={supportedLocaleCode.es}
        labelText={getNativeLanguageName("spanish")}
        focus={false}
        close={() => {}}
        disabled={false}
      />
      <LocaleMenuEntry
        code={supportedLocaleCode.ilo}
        labelText={getNativeLanguageName("ilocano")}
        focus={false}
        close={() => {}}
        disabled={false}
      />
      <LocaleMenuEntry
        code={supportedLocaleCode.tl}
        labelText={getNativeLanguageName("tagalog")}
        focus={false}
        close={() => {}}
        disabled={false}
      />
    </div>
  );

  return (
    <Disclosure as="div" className="relative">
      <DisclosureButton
        as="div"
        ref={(node) => {
          buttonRef.current = node as HTMLElement | null;
        }}
        className="w-full"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
        }}
      >
        {({ open }) => (
          <Label
            as="span"
            className={clsx(
              "flex items-center justify-between py-3 px-2 rounded-md w-full text-sm font-medium hover:bg-gray-100",
              open ? "bg-gray-100" : "dark:text-white hover:dark:text-inherit",
            )}
          >
            {t("ui_general_language")}
            <span
              className={clsx(
                "icon-[ic--round-keyboard-arrow-right] h-5 w-5 transition-transform",
                open ? "transform rotate-90" : "",
              )}
            />
          </Label>
        )}
      </DisclosureButton>
      {isMobile ? (
        <DisclosurePanel
          className="absolute left-0 top-full mt-1 w-48 rounded-md bg-white dark:bg-gray-900 shadow-lg ring-1 ring-black ring-opacity-5"
        >
          {renderPanelContent()}
        </DisclosurePanel>
      ) : (
        <>
          <DisclosurePanel className="hidden">
            {({ open }) => {
              if (isOpen !== open) {
                setTimeout(() => setIsOpen(open), 0);
              }
              return <></>;
            }}
          </DisclosurePanel>
          {isOpen &&
            createPortal(
              <div
                ref={portalRef}
                className="fixed w-48 rounded-md bg-white dark:bg-gray-900 shadow-lg ring-1 ring-black ring-opacity-5 z-[9999]"
                style={panelStyle}
              >
                {renderPanelContent()}
              </div>,
              document.body
            )}
        </>
      )}
    </Disclosure>
  );
};

export default LanguageSelectionMenu;
